import { Link } from '@tanstack/react-router';
import { CircleArrowLeft, Copy, Link2Icon } from 'lucide-react';
import { toast } from 'sonner';

import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';
import { copyToClipboard } from '~shared/utils/clipboard';

import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import { Input } from '~shared/shadcn/ui/input';

export function ShareParentPortalLink() {
  const { data } = useGetStudioDetails();
  const registrationLink = `${import.meta.env.VITE_PARENT_PORTAL_URL}/login?studioId=${data?.locationId}`;

  const handleCopyLink = async () => {
    const success = await copyToClipboard(registrationLink);
    if (success) {
      toast.success('Link copied!', {
        description: 'The registration link has been copied to your clipboard.',
      });
    } else {
      toast.error('Failed to copy', {
        description: 'Please try copying the link manually.',
      });
    }
  };

  const shareOnSocial = (platform: string) => {
    const text = encodeURIComponent(
      'Register for classes through our online portal!',
    );
    const url = encodeURIComponent(registrationLink);

    const links = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    };

    window.open(links[platform as keyof typeof links], '_blank');
  };

  return (
    <div className='min-h-screen bg-background p-4 md:p-8 lg:p-12'>
      <Link to='/dashboard' className='flex w-fit items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Parent Portal</h2>
      </Link>
      <Card className='mx-auto max-w-2xl'>
        <CardHeader>
          <CardTitle className='text-2xl md:text-3xl'>
            Parent Portal & Online Registration
          </CardTitle>
          <CardDescription className='text-base md:text-lg'>
            Share this link on your website, social media, or anywhere else
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='space-y-2'>
            <p className='text-sm text-muted-foreground'>
              Parents and students can use this link to register for classes,
              pay tuition, and communicate with staff.
            </p>
            <div className='flex space-x-2'>
              <Input
                readOnly
                value={registrationLink}
                className='font-mono text-sm'
              />
              <Button
                variant='secondary'
                size='icon'
                onClick={handleCopyLink}
                aria-label='Copy registration link'
              >
                <Copy className='size-4' />
              </Button>
            </div>
          </div>

          <div className='space-y-3'>
            <h3 className='text-sm font-medium'>Share on social media</h3>
            <div className='flex space-x-2'>
              <Button
                variant='outline'
                size='sm'
                className='flex items-center space-x-2'
                onClick={() => {
                  shareOnSocial('facebook');
                }}
              >
                {/* <Facebook className='size-4' /> */}
                <span>Facebook</span>
              </Button>
              <Button
                variant='outline'
                size='sm'
                className='flex items-center space-x-2'
                onClick={() => {
                  shareOnSocial('twitter');
                }}
              >
                {/* <Twitter className='size-4' /> */}
                <span>Twitter</span>
              </Button>
              <Button
                variant='outline'
                size='sm'
                className='flex items-center space-x-2'
                onClick={handleCopyLink}
              >
                <Link2Icon className='size-4' />
                <span>Copy Link</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
